import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import supabase from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, navigate]);

  // Dark theme colors matching your app
  const accentPrimary = '#BB86FC';
  const accentSecondary = '#03DAC6';
  const glowColor = 'rgba(187, 134, 252, 0.15)';

  const supabaseTheme = {
    colors: {
      brand: accentPrimary,
      brandAccent: accentSecondary,
      brandButtonText: '#ffffff',
      defaultButtonBackground: 'rgba(255,255,255,0.05)',
      defaultButtonBackgroundHover: 'rgba(255,255,255,0.1)',
      defaultButtonBorder: 'rgba(255,255,255,0.1)',
      defaultButtonText: 'rgba(255,255,255,0.9)',
      dividerBackground: 'rgba(255,255,255,0.08)',
      inputBackground: 'rgba(255,255,255,0.03)',
      inputBorder: 'rgba(255,255,255,0.1)',
      inputBorderHover: 'rgba(255,255,255,0.2)',
      inputBorderFocus: accentPrimary,
      inputText: 'rgba(255,255,255,0.9)',
      inputPlaceholder: 'rgba(255,255,255,0.4)',
      messageText: 'rgba(255,255,255,0.6)',
      messageTextDanger: '#CF6679',
      anchorTextColor: accentPrimary,
      anchorTextHoverColor: accentSecondary,
    },
    fonts: {
      bodyFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      buttonFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      inputFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
      labelFontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace",
    },
    fontSizes: {
      baseBodySize: '0.9rem',
      baseInputSize: '0.9rem',
      baseLabelSize: '0.8rem',
      baseButtonSize: '0.85rem',
    },
    radii: {
      borderRadiusButton: '12px',
      buttonBorderRadius: '12px',
      inputBorderRadius: '12px',
    },
    borders: {
      inputBorderWidth: '1.5px',
      buttonBorderWidth: '1.5px',
    },
    space: {
      spaceSmall: '8px',
      spaceMedium: '12px',
      spaceLarge: '20px',
      labelBottomMargin: '8px',
      anchorBottomMargin: '8px',
      emailInputSpacing: '8px',
      socialAuthSpacing: '12px',
      buttonPadding: '12px 16px',
      inputPadding: '14px 16px',
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a0a0b',
        p: 2,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: '20px',
          border: '1.5px solid rgba(255,255,255,0.1)',
          background: 'linear-gradient(135deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.99) 100%)',
          boxShadow: `0 0 0 1px ${glowColor}, 0 20px 60px -15px rgba(0,0,0,0.5)`,
          backdropFilter: 'blur(12px)',
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentSecondary} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5,
              boxShadow: `0 8px 32px rgba(187, 134, 252, 0.4)`,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.05)',
                boxShadow: `0 12px 40px rgba(187, 134, 252, 0.5)`,
              },
            }}
          >
            <Box
              sx={{
                fontFamily: 'Candal',
                fontSize: '2rem',
                fontWeight: 400,
                color: '#fff',
                lineHeight: 1,
              }}
            >
              {'{r}'}
            </Box>
          </Box>
          <Box
            sx={{
              fontFamily: 'Candal',
              fontSize: '1.75rem',
              fontWeight: 400,
              mb: 0.75,
              background: `linear-gradient(135deg, ${accentPrimary} 0%, ${accentSecondary} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            reciply admin
          </Box>
          <Box
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            sign in to access the admin panel
          </Box>
        </Box>

        {/* Supabase Auth UI */}
        <Auth
          redirectTo={window.location.origin}
          supabaseClient={supabase}
          appearance={{
            theme: ThemeSupa,
            variables: {
              dark: supabaseTheme,
            },
          }}
          theme="dark"
          providers={['google', 'github']}
          localization={{
            variables: {
              sign_in: {
                email_label: 'email',
                email_input_placeholder: 'email address',
                password_label: 'password',
                password_input_placeholder: 'password',
                social_provider_text: '{{provider}}',
                button_label: 'sign in',
              },
              forgotten_password: {
                link_text: 'forgot password',
              },
              sign_up: {
                link_text: 'sign up',
              },
              provider_names: {
                google: 'google',
                github: 'github',
              },
            },
          }}
        />

        <Box
          sx={{
            textAlign: 'center',
            mt: 3,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.7rem',
            color: 'rgba(255,255,255,0.4)',
          }}
        >
          admin access only
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
